import { EmptyState } from "../EmptyState";

import type { ElementSnippet, StepData } from "../Workflow";
import { type BrowserLinkItem, BrowserChrome } from "../BrowserChrome";
import { useNavigation } from "./useNavigation";
import { Page } from "./Page";
import { useMemo } from "react";
import useInspectMode from "./useInspectMode";
import useAgentControl from "./useAgentControl";
import useAgentControlBridge from "./useAgentControlBridge";

type Props = {
  step: StepData;
  spaceSlug: string;
  spaceId: string;
  branchId: string;
  contentKey?: string;
  navBranchId?: string;
  displayUrl: string;
  openUrl?: string;
  browserLinks?: ReadonlyArray<BrowserLinkItem>;
  inspectMode?: boolean;
  elementSnippets?: readonly ElementSnippet[];
  onInspectModeChange?: (mode: boolean) => void;
  onElementSelect?: (
    element: Omit<ElementSnippet, "stepName" | "color">
  ) => void;
  onElementDeselect?: (index: number) => void;
};

export function LiveBrowser({
  step,
  spaceSlug,
  spaceId,
  branchId,
  contentKey,
  navBranchId,
  displayUrl,
  openUrl,
  browserLinks,
  inspectMode,
  onInspectModeChange,
  onElementSelect,
  onElementDeselect,
  elementSnippets,
}: Props) {
  const inspect = useInspectMode({
    enabled: inspectMode,
    stepColor: step.color,
    onElementSelect,
    onElementDeselect,
    elementSnippets,
  });
  const baseRoute = step.publicRoute ?? "";
  const navigation = useNavigation(baseRoute, navBranchId);
  const agentControl = useAgentControl();
  const features = useMemo(
    () => [inspect.feature, navigation.feature, agentControl.feature],
    [inspect.feature, navigation.feature, agentControl.feature]
  );

  const previewHost = displayUrl.split("/")[0] ?? "";
  const navDisplayUrl = previewHost + navigation.path;

  useAgentControlBridge(agentControl, step.name, !!step.publicRoute);

  return (
    <BrowserChrome
      displayUrl={navDisplayUrl}
      openUrl={openUrl}
      links={browserLinks}
      inspectMode={inspectMode}
      canBack={navigation.canBack}
      canForward={navigation.canForward}
      onBack={navigation.back}
      onForward={navigation.forward}
      onReload={navigation.reload}
      onInspectModeChange={onInspectModeChange}
    >
      {step.publicRoute ? (
        <Page
          key={contentKey ?? ""}
          spaceId={spaceId}
          spaceSlug={spaceSlug}
          publicRoute={step.publicRoute}
          branchId={branchId}
          externalId={step.routeExternalId}
          features={features}
        />
      ) : (
        <EmptyState
          iconName="earth"
          iconSize={24}
          maxWidth={220}
          title="No page yet"
          description="This step doesn’t serve a webpage on this branch."
        />
      )}
    </BrowserChrome>
  );
}
